/**
 * LeftSidebar - Main left sidebar container
 * Compact sidebar with series thumbnails and inline presets
 * Supports collapsed state via ',' keyboard shortcut
 */

import React, { useState, useMemo, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { SeriesThumbnailGrid } from "./SeriesThumbnailGrid";
import { WindowPresetBar, WindowPreset } from "./WindowPresetBar";
import { useSeriesThumbnails } from "../../hooks/useSeriesThumbnails";
import { StudyMetadataResponse } from "../../utils/dicomMetadataLoader";
import {
  slotAssignmentsSelector,
  activeSlotAtom,
  slotStateFamily,
  toggleInvertAtom,
  resetSlotViewAtom,
} from "../../atoms/hangingProtocolAtoms";
import { CollapsedSidebarStrip } from "./CollapsedSidebarStrip";
import { helpDialogOpenAtom, clearAllMeasurementsConfirmAtom } from "../../atoms/measurementAtoms";
import { FaQuestionCircle } from "../../utils/icons";
import { ViewerToolbar } from "../hanging-protocol/ViewerToolbar";
import { ViewModeSelector } from "./ViewModeSelector";
import { ScreenRecorder } from "../ScreenRecorder";
import { userTypeAtom } from "../../atoms/authAtoms";
import { isDemoViewer } from "../../services/roles";
import {
  getCornerstone,
  getCornerstoneTools,
  findHangingProtocolViewport,
} from "../../utils/dicomLoader";
import { logger } from "@/utils/logger";
import { nnSegmentationResultAtom } from "../../atoms/segmentationAtoms";

const StudyIdPanel: React.FC<{ studyId: string }> = ({ studyId }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-2.5 py-1.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left text-[9px] text-fg-muted hover:text-fg transition-colors"
      >
        <span className="sidebar-text-label">Study UID</span>
        <span className="ml-1 opacity-50">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && (
        <div className="text-[9px] text-fg-muted leading-tight break-all font-mono opacity-70 mt-1 select-all">
          {studyId}
        </div>
      )}
    </div>
  );
};

interface LeftSidebarProps {
  studyMetadata: StudyMetadataResponse | undefined;
  studyId?: string;
  selectedSeriesId?: string | null;
  activeWindowPreset?: WindowPreset | null;
  onSeriesSelect?: (seriesId: string) => void;
  onWindowPresetSelect: (preset: WindowPreset, center: number, width: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Optional extra content rendered above the a2z logo footer */
  footerExtra?: React.ReactNode;
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  studyMetadata,
  studyId,
  selectedSeriesId,
  activeWindowPreset,
  onSeriesSelect,
  onWindowPresetSelect,
  isCollapsed = false,
  onToggleCollapse,
  footerExtra,
}) => {
  const { thumbnails, retry: retryThumbnail } = useSeriesThumbnails(studyMetadata);
  const userType = useAtomValue(userTypeAtom);
  const slotAssignments = useAtomValue(slotAssignmentsSelector);
  const setHelpDialogOpen = useSetAtom(helpDialogOpenAtom);
  const setClearAllMeasurementsConfirm = useSetAtom(clearAllMeasurementsConfirmAtom);
  const setNnSegResult = useSetAtom(nnSegmentationResultAtom);

  // Toolbar state - uses active slot from hanging protocol
  const activeSlot = useAtomValue(activeSlotAtom);
  const activeSlotState = useAtomValue(slotStateFamily(activeSlot));
  const toggleInvert = useSetAtom(toggleInvertAtom);
  const resetSlotView = useSetAtom(resetSlotViewAtom);

  const handleResetView = useCallback(() => {
    resetSlotView(activeSlot);
  }, [resetSlotView, activeSlot]);

  const handleInvert = useCallback(() => {
    toggleInvert(activeSlot);
  }, [toggleInvert, activeSlot]);

  // L/R (horizontal) flip applied to the active slot's viewport only — mirrors
  // the active-slot scope of Reset/Invert above. Flip is a camera-level toggle
  // with no atom-backed state (same imperative path as the resetSlotView
  // handler in ProtocolViewport), so we drive Cornerstone directly here.
  const handleFlipHorizontal = useCallback(() => {
    const vp = findHangingProtocolViewport(activeSlot);
    if (!vp?.getCamera) {
      logger.warn("[LeftSidebar] flip skipped: no active viewport for slot=%s", activeSlot);
      return;
    }
    try {
      const { flipHorizontal } = vp.getCamera();
      vp.setCamera({ flipHorizontal: !flipHorizontal });
      vp.render();
    } catch (err) {
      logger.warn("[LeftSidebar] flip horizontal failed slot=%s err=%o", activeSlot, err);
    }
  }, [activeSlot]);

  const handleDeleteAnnotation = useCallback(() => {
    try {
      const cornerstone = getCornerstone();
      const cornerstoneTools = getCornerstoneTools();
      const selection = cornerstoneTools.annotation.selection;
      const selected = selection.getAnnotationsSelected();
      if (selected && selected.length > 0) {
        cornerstoneTools.annotation.state.removeAnnotation(selected[0]);
        cornerstone.getRenderingEngines()?.forEach((engine: any) => engine.render());
        // Clear 3D viewer — contours changed outside nnInteractive flow,
        // cached lastResponse is stale so we can't re-derive.
        setNnSegResult(null);
      }
    } catch {
      // Cornerstone not initialized or no selection
    }
  }, []);

  // Clear ALL measurement annotations (length/ruler + rectangle ROI) across the
  // study. Destructive + study-wide, so the button opens a confirm dialog rather
  // than deleting inline; the actual removal lives in the clearAllMeasurements
  // command (shared with the keyboard shortcut and the right-click menu) and is
  // scoped to measurement tools so segmentation / region-grow / nnInteractive
  // contours survive.
  const handleClearAllAnnotations = useCallback(() => {
    setClearAllMeasurementsConfirm(true);
  }, [setClearAllMeasurementsConfirm]);

  // Get all series that are assigned to any viewport slot
  // If selectedSeriesId prop is provided (simple mode), use that instead of hanging protocol
  const assignedSeriesIds = useMemo(() => {
    // Simple mode: use provided selectedSeriesId
    if (selectedSeriesId) {
      return new Set<string>([selectedSeriesId]);
    }

    // Hanging protocol mode: use slot assignments
    const ids = new Set<string>();
    Object.values(slotAssignments).forEach((seriesId) => {
      if (seriesId) ids.add(seriesId);
    });
    return ids;
  }, [slotAssignments, selectedSeriesId]);

  // Collapsed state - show minimal strip
  if (isCollapsed) {
    return (
      <CollapsedSidebarStrip side="left" onExpand={() => onToggleCollapse?.()} shortcutHint="," />
    );
  }

  return (
    <div
      className="w-[160px] shrink-0 h-full sidebar-premium sidebar-premium-border-r"
      data-testid="left-sidebar"
    >
      {/* Viewer Tools - fixed at top */}
      <div className="px-1.5 py-2">
        <ViewerToolbar
          onResetView={handleResetView}
          onInvert={handleInvert}
          onFlipHorizontal={handleFlipHorizontal}
          onDeleteAnnotation={handleDeleteAnnotation}
          onClearAllAnnotations={handleClearAllAnnotations}
          isInverted={activeSlotState.isInverted}
          className="w-full justify-center"
        />
      </div>

      {/* View-mode selector — renders only for hybrid projects (>1 gating mode) */}
      <ViewModeSelector />

      {/* Window Presets - fixed below toolbar, always visible */}
      <WindowPresetBar activePreset={activeWindowPreset} onPresetSelect={onWindowPresetSelect} />

      <div className="sidebar-divider" />

      {/* Single scrollable area for everything */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Study ID - collapsible to save space */}
        {studyId && <StudyIdPanel studyId={studyId} />}

        {/* Series Thumbnails */}
        <SeriesThumbnailGrid
          thumbnails={thumbnails}
          assignedSeriesIds={assignedSeriesIds}
          onSeriesClick={onSeriesSelect}
          onRetry={retryThumbnail}
          isLoading={!studyMetadata}
          seriesMetadata={studyMetadata?.series_metadata}
        />

        {/* Help Button */}
        <div className="px-2 py-2">
          <div className="sidebar-divider mb-2" />
          <button
            onClick={() => setHelpDialogOpen(true)}
            className="sidebar-btn w-full px-2 py-2 text-[10px] font-medium text-fg-muted hover:text-fg gap-1.5"
            title="Open keyboard shortcuts (Shift+?)"
          >
            <FaQuestionCircle className="w-3 h-3" />
            <span>Shortcuts</span>
            <span className="text-[8px] text-fg-muted font-mono">⇧?</span>
          </button>
        </div>

        {/* Screen Recorder */}
        {studyId && !isDemoViewer(userType) && (
          <div className="px-2 py-2">
            <div className="sidebar-divider mb-2" />
            <ScreenRecorder studyId={studyId} compact />
          </div>
        )}

        {/* Optional extra footer content (e.g., Stats Dashboard button) */}
        {footerExtra && (
          <div className="px-2 py-2">
            <div className="sidebar-divider mb-2" />
            {footerExtra}
          </div>
        )}

        {/* Brand Footer */}
        <div className="px-2 py-3 mt-auto">
          <div className="sidebar-divider mb-3" />
          <div className="flex items-center justify-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
            <img src="/images/a2z-logo-linear.png" alt="a2z Radiology AI" className="h-4 w-auto" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftSidebar;
